/**
 * 云空间上传文件进度列表组件
 */
import React, {
  ReactChild,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef
} from "react";
// import Icon from '@/components/icon';
import { message } from "antd";
import "./index.less";
import classNames from "classnames";
import { TaskState } from "./types";
import { sizeToStr } from "./utils";
import { UploadControl, UploadControlCenterInstance } from "./UploadControl";
import UploadItem from "./UploadItem";
import { useTranslation } from "@/components/Joyspace/context";
import okPng from "./imgs/ok.png";
import cancelPng from "./imgs/cancel.png";
import failPng from "./imgs/fail.png";
import Prompt from "@jd/focus-desktop-comps/lib/es/PromptModal";
import { useLocation } from "react-router-dom";
import Icons, { CheckCircleTwoTone } from "@/utils/antdIcon";
import events from "../Joyspace/context/events";
import {
  CancelIcon,
  FailAllIcon,
  RetrAllIcon,
  SuccessAllIcon,
  UpIcon
} from "../Joyspace/components/BatchDownloadPanel/BasisComponents/Icons";
import Icon from "../Joyspace/Icon";
import { getFileDownloadBzOtherType, IBizOptions } from "@/utils/chat/message";
function UploadBox(
  props: {
    headerTitle?: React.ReactNode; // 定制化header文案
    toJoysSapce?: boolean; // 是否上传到文档
    uploadControlName: string; // 上传任务模块
    appKey?: string; // 上传appKey
    onItemClick?: Function; // 点击上传面板行响应事件
    className?: string;
    retryLimitCallBackFunc?: Function; // retry时数量控制回调
    uploadFailCallBackFunc?: Function; // 上传失败回调
    uploadSuccessCallBackFunc?: Function; // 上传成功回调
    zeroSizeLimitCallBackFunc?: Function; // 上传空文件回调
    bizOptions?: IBizOptions // 业务参数
  },
  ref: any
) {
  const {
    toJoysSapce = false,
    uploadControlName,
    headerTitle = null,
    onItemClick,
    className,
    appKey = "",
    zeroSizeLimitCallBackFunc,
    bizOptions
  } = props;
  const lang = useTranslation();
  const [uploadControlInstance, setUploadControlInstance] = useState<UploadControl | null>(null);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [panelShow, setPanelShow] = useState(false);
  const visibleRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<TaskState>(TaskState.PENDING);
  const [currentCount, setCurrentCount] = useState(0);
  const [stateCount, setStateCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  const location = useLocation();
  const uploadSizeRef = useRef(0);
  const preUploadSizeRef = useRef(0);
  const noFreshTimesRef = useRef(0);
  const contentActiveClass = classNames({
    "ant-collapse-content": true,
    "ant-collapse-content-active": panelShow,
    "ant-collapse-content-inactive": !panelShow
  });

  const collapseHeaderClass = classNames({
    "ant-collapse-header": true,
    "ant-collapse-header-progress": true
    // 'border-bottom-less': panelShow,
  });
  const repeatIconClass = classNames({
    "repeat-icon": true,
    rotate: TaskState.UPLOADING === status || TaskState.PENDING === status
  });

  const upDownOperate = () => {
    events.emit("handleDownloadPanel", !panelShow);
    setPanelShow(!panelShow);
  };

  const headerText: any = {
    [TaskState.UPLOADING]: lang("myspace.upload_uploading"),
    [TaskState.PENDING]: lang("myspace.upload_uploading"),
    [TaskState.COMPLETED]: lang("myspace.upload_success"),
    [TaskState.FAILED]: lang("myspace.upload_failed"),
    [TaskState.CANCEL]: lang("myspace.upload_cancelled")
  };
  const headerIcon: any = {
    [TaskState.UPLOADING]: "iconic_repeat",
    [TaskState.PENDING]: "iconic_repeat",
    [TaskState.COMPLETED]: "iconic_repeat",
    [TaskState.FAILED]: "iconic_repeat",
    [TaskState.CANCEL]: "iconic_repeat"
  };

  const onStateChange = useCallback(
    (_fileId: string, _state: TaskState, _progressSize: number) => {
      if (!uploadControlInstance) {
        return;
      }

      const state = uploadControlInstance.getStateInfo();
      setStatus(state.status);
      setCurrentCount(state.currentCount);
      setStateCount(state.stateCount);
      if (_progressSize > 0) {
        uploadSizeRef.current += uploadSizeRef.current + _progressSize;
      }
    },
    [uploadControlInstance]
  );
  const setVisibleFunc = useCallback(
    (isVisible: boolean) => {
      visibleRef.current = isVisible;
      setVisible(isVisible);
      if (isVisible && uploadControlInstance) {
        const state = uploadControlInstance.getStateInfo();
        setStatus(state.status);
        setCurrentCount(state.currentCount);
        setStateCount(state.stateCount);
      }
    },
    [setVisible, uploadControlInstance, setStatus, setCurrentCount, setStateCount]
  );
  const errorControlEventListener = useCallback(
    (errMsg) => {
      const { errCode } = errMsg;
      if (errCode === "zeroSize") {
        if (zeroSizeLimitCallBackFunc) {
          zeroSizeLimitCallBackFunc?.();
        } else {
          message.warn(lang("myspace.noZeroSize"));
        }
      }
    },
    [lang, zeroSizeLimitCallBackFunc]
  );

  const uploadControlEventListener = useCallback(
    ({ fileIds }: { fileIds: string[] }) => {
      if (!visibleRef.current && location.pathname.indexOf("joyspace/home") > -1) {
        message.info(lang("myspace.upload_to_my_space"));
      }
      setFileIds(fileIds);
      setPanelShow(true);
      setVisibleFunc(true);

      setTotalCount(fileIds.length);
      events.emit("handleDownloadPanel", true);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setVisibleFunc, setFileIds, setPanelShow, lang, location.pathname]
  );

  useMemo(() => {
    const isJoyspace = location.pathname.indexOf("/joyspace") > -1;
    if (uploadControlInstance?.checkIsToJoySpace()) {
      if (fileIds.length > 0 && isJoyspace && !visibleRef.current) {
        setVisibleFunc(true);
      }
      if (visibleRef.current && !isJoyspace) {
        setVisibleFunc(false);
      }
    }
  }, [fileIds, location, uploadControlInstance, setVisibleFunc]);

  useEffect(() => {
    if (!uploadControlInstance) {
      return;
    }
    uploadControlInstance?.on("upload-control-event", uploadControlEventListener);
    uploadControlInstance?.on("error-control-event", errorControlEventListener);
    return () => {
      uploadControlInstance?.off("upload-control-event", uploadControlEventListener);
      uploadControlInstance?.off("error-control-event", errorControlEventListener);
    };
  }, [errorControlEventListener, uploadControlEventListener, uploadControlInstance]);

  useEffect(() => {
    setUploadControlInstance(
      UploadControlCenterInstance.register(uploadControlName, toJoysSapce, appKey, bizOptions)
    );
    return () => {
      UploadControlCenterInstance.clear(uploadControlName);
    };
  }, [uploadControlName, toJoysSapce, appKey]);
  const headerIconNode = useMemo(() => {
    let node = <Icon className={repeatIconClass} type={headerIcon[status]} />;
    switch (status) {
      case TaskState.COMPLETED:
        node = <Icons component={SuccessAllIcon} />;
        break;
      case TaskState.FAILED:
        node = <Icons component={FailAllIcon} />;
        break;
      case TaskState.CANCEL:
        node = <Icons component={RetrAllIcon} />;
        break;
      default:
        break;
    }
    return node;
  }, [status, headerIcon, repeatIconClass]);
  const collapseHeaderPart = useMemo(() => {
    if (!status) {
      return null;
    }
    if (!visible) {
      return null;
    }
    let countNode = null;
    let text = headerText[status];

    if (
      !(
        status === TaskState.COMPLETED ||
        status === TaskState.FAILED ||
        status === TaskState.CANCEL
      )
    ) {
      countNode = (
        <span>
          {currentCount}/{totalCount}
        </span>
      );
    }
    if (status === TaskState.CANCEL) {
      text = `${text}`;
    } else if (
      stateCount !== totalCount &&
      status !== TaskState.PENDING &&
      status !== TaskState.UPLOADING
    ) {
      text = `部分${text}`;
    } else if (
      stateCount === totalCount &&
      status !== TaskState.PENDING &&
      status !== TaskState.UPLOADING
    ) {
      text = `全部${text}`;
    }
    if (totalCount === 0) {
      text = "";
    }
    return (
      <div className="panel-header-space">
        {headerIconNode}
        <span className="panel-header panel-header-progress">
          {headerTitle}
          {text}
          {countNode}
        </span>
      </div>
    );
  }, [
    status,
    visible,
    headerText,
    headerIconNode,
    headerTitle,
    currentCount,
    totalCount,
    stateCount
  ]);

  const stopUpload = useCallback(() => {
    setVisibleFunc(false);
    setFileIds([]); // 关闭上传窗口时清空FileIds
    uploadControlInstance?.clearAllUpload();
  }, [uploadControlInstance, setVisibleFunc]);

  const closeCollapse = useCallback(
    (force) => {
      // eslint-disable-next-line no-debugger
      // debugger;
      // 判断上传状态
      // 状态为上传完成 上传失败，直接关闭
      // 状态为已取消提示：文件尚未上传完成，是否取消上传文件？
      // 状态为上传中时提示：文件正在上传中，确认取消所有文件上传吗？
      // setVisible(false);
      // uploadControlInstance.clearAllUpload();
      const state = uploadControlInstance?.getStateInfo()?.status || TaskState.INITED;
      if (
        state === TaskState.COMPLETED ||
        state === TaskState.FAILED ||
        state === TaskState.CANCEL ||
        force
      ) {
        stopUpload();
      } else {
        // 状态为已取消提示：文件尚未上传完成，是否取消上传文件？
        Prompt({
          title: lang("myspace.upload_cancel"),
          icon: <Icon type="iconic_failure" style={{ color: "#F96137" }} />,
          onOk: stopUpload,
          content: lang("myspace.upload_progress_tip"),
          okText: lang("myspace.Confirm_Text"),
          cancelText: lang("myspace.Cancel_Text")
        });
      }
    },
    [lang, stopUpload, uploadControlInstance]
  );
  useImperativeHandle(
    ref,
    () => {
      return {
        getUploadInstance: () => {
          return uploadControlInstance;
        },
        close: () => {
          closeCollapse(1);
        }
      };
    },
    [closeCollapse, uploadControlInstance]
  );
  const handleUploadPanel = useCallback((isDownloadExpand) => {
    isDownloadExpand && setPanelShow(!isDownloadExpand);
  }, []);
  // 下载面板展示关闭通知
  useEffect(() => {
    events.on("handleUploadPanel", handleUploadPanel);
    return () => {
      events.removeListener("handleUploadPanel", handleUploadPanel);
    };
  }, [handleUploadPanel]);
  // 清空虚拟的上传速度
  const onCancelAll = useCallback(() => {
    uploadControlInstance?.cancelAllUpload();
  }, [uploadControlInstance]);

  const collapseOperatePart = useMemo(() => {
    return (
      <div className="panel-operate">
        <span
          className={
            panelShow ? "collapse-icon collapse-down-icon" : "collapse-icon collapse-up-icon"
          }
        >
          <Icons component={UpIcon} />
        </span>
        <span className="collapse-cancel-icon">
          <Icons
            component={CancelIcon}
            onClick={(e) => {
              e.stopPropagation();
              closeCollapse(0);
            }}
          />
        </span>
      </div>
    );
  }, [panelShow]); //eslint-disable-line

  if (!visible) {
    return null;
  }

  if (!uploadControlInstance) {
    return null;
  }

  return (
    <div className={`${className} global-upload-controller`}>
      <div className="upload-progress-list">
        <div className="ant-collapse ant-collapse-borderless">
          <div className="ant-collapse-item ant-collapse-item-disabled ant-collapse-no-arrow ant-collapse-item-progress">
            <div className={collapseHeaderClass} onClick={upDownOperate}>
              {collapseHeaderPart}
              <div className="ant-collapse-extra ant-collapse-extra-progress">
                {collapseOperatePart}
              </div>
            </div>
            {panelShow && (
              <div className="file-upload-sum">
                <div className="file-upload-count">
                  {`已上传 ${currentCount}/${totalCount}文件`}
                </div>
                {status === TaskState.UPLOADING && (
                  <a className="file-canel-all" onClick={onCancelAll}>
                    {lang("myspace.cancelAllText")}
                  </a>
                )}
              </div>
            )}
            <div id="file-progress-container" className={contentActiveClass}>
              {fileIds.map((id) => (
                <UploadItem
                  t={lang}
                  onItemClick={onItemClick}
                  uploadControlInstance={uploadControlInstance}
                  fileId={id}
                  status={status}
                  key={id}
                  onStateChange={onStateChange}
                  uploadSuccessCallBackFunc={props.uploadSuccessCallBackFunc}
                  uploadFailCallBackFunc={props.uploadFailCallBackFunc}
                  retryLimitCallBackFunc={props.retryLimitCallBackFunc}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default forwardRef(UploadBox);
